import { useId, useState } from "react";
import { Eye, FilePenLine } from "lucide-react";
import ProjectReportIntroPreview from "./ProjectReportIntroPreview";
import { useAppStore } from "lib/store/useAppStore";
import { postUpdateProject } from "lib/api/postUpdateProject";

const FIELDS = [
  ["project_name", "프로젝트명"], ["contract_name", "용역명"],
  ["period_start", "시작일", "date"], ["period_end", "종료일", "date"],
  ["contractor", "용역업체명"], ["participants", "참여인원"],
  ["purpose", "진단 목적", "textarea"], ["analysis_result", "진단결과 분석의견", "textarea"],
];

export default function ProjectReportInfoForm({ projectId, project, onSaved, listScope }) {
  const [previewOpen, setPreviewOpen] = useState(false);
  const inputId = useId();
  const formId = `${inputId}-form`;
  const user = useAppStore(state => state.user);
  const setLastSelectedProject = useAppStore(state => state.setLastSelectedProject);
  const fetchProjectTree = useAppStore(state => state.fetchProjectTree);
  const [form, setForm] = useState(() => ({ id: projectId,
    ...Object.fromEntries(FIELDS.map(([key, , type]) => [key, type === "date"
      ? String(project[key] ?? "").slice(0, 10) : project[key] ?? ""])),
  }));
  const [saving, setSaving] = useState(false);
  const [savedForm, setSavedForm] = useState(form);
  const [editing, setEditing] = useState(false);
  const dirty = JSON.stringify(form) !== JSON.stringify(savedForm);
  const [message, setMessage] = useState("");
  const canEdit = Boolean(user?.id && project.user_id && String(project.user_id).toLowerCase() === String(user.id).toLowerCase());
  const save = async event => {
    event.preventDefault();
    if (saving || !canEdit || !editing || !dirty) return;
    if (form.period_start && form.period_end && form.period_start > form.period_end) {
      setMessage("종료일은 시작일보다 빠를 수 없습니다."); return;
    }
    setSaving(true);
    try {
      const result = await postUpdateProject(form);
      if (!result.success) { setMessage(result.message || "저장에 실패했습니다."); return; }
      onSaved({ ...project, ...form });
      setLastSelectedProject(projectId, form.project_name);
      setSavedForm(form);
      setEditing(false);
      setMessage("기재 정보를 저장했습니다. 미리보기에도 반영됩니다.");
      await fetchProjectTree(user.id, projectId);
    } catch { setMessage("저장 또는 목록 갱신에 실패했습니다. 다시 확인해 주세요."); }
    finally { setSaving(false); }
  };
  return <section className="projectReportInfo projectReportScope" aria-label="기재 정보">
    <div className="projectReportInfo__heading"><h3><FilePenLine size={18} aria-hidden="true" />기재 정보</h3>
      <div className="projectReportInfo__headingActions">
        {canEdit && (!editing ? <button type="button" className="projectSettings__secondary" onClick={() => { setEditing(true); setMessage(""); }}>수정</button> : <>
          <button type="button" className="projectSettings__secondary" disabled={saving} onClick={() => { setForm(savedForm); setEditing(false); setMessage(""); }}>취소</button>
          <button type="submit" form={formId} className="btn-bg-blue" disabled={!dirty || saving}>{saving ? "저장 중…" : "저장"}</button>
        </>)}
        <button type="button" className="projectSettings__secondary" onClick={() => setPreviewOpen(true)}><Eye size={16} aria-hidden="true" />미리보기</button>
      </div>
    </div>
    <p>표지와 본문에 표시됩니다. 변경한 내용은 저장 후 미리보기에 반영됩니다.</p>
    {!canEdit && <p className="projectSettings__message">기재 정보는 프로젝트 소유자만 수정할 수 있습니다.</p>}
    <form id={formId} onSubmit={save}>
      <dl className="projectReportInfo__values">
        {FIELDS.map(([key, label, type]) => <div key={key} className={type === "textarea" ? "is-wide" : ""}>
          <dt>{editing ? <label htmlFor={`${inputId}-${key}`}>{label}</label> : label}</dt>
          <dd>{!editing ? savedForm[key] || "미입력" : type === "textarea" ? <textarea id={`${inputId}-${key}`} rows={2} value={form[key]} disabled={!canEdit || saving} onChange={event => setForm({ ...form, [key]: event.target.value })} /> :
            <input id={`${inputId}-${key}`} type={type || "text"} value={form[key]} required={key === "project_name"} maxLength={key === "participants" ? 500 : 255}
              disabled={!canEdit || saving} onChange={event => setForm({ ...form, [key]: event.target.value })} />}</dd>
        </div>)}
      </dl>
    </form>
    {message && <p role="status" className="projectSettings__message">{message}</p>}
    {previewOpen && <ProjectReportIntroPreview projectId={projectId} project={project} listScope={listScope} onClose={() => setPreviewOpen(false)} />}
  </section>;
}
